import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme: _defaultTheme = 'light',
  storageKey: _storageKey = 'orbii-admin-ui-theme',
  ...props
}: ThemeProviderProps) {
  // Force light mode only - dark mode styling preserved but not active
  const [theme] = useState<Theme>('light');

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');
    // Always use light mode
    root.classList.add('light');
  }, []);

  const value = {
    theme,
    // setTheme is a no-op since we're forcing light mode
    setTheme: (_theme: Theme) => {
      // Dark mode styling preserved - uncomment below to re-enable theme switching
      // localStorage.setItem(storageKey, _theme);
      // setTheme(_theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
